import { useEffect, useState, useMemo, useCallback } from 'react';
import BIBLE_DATA from './niv.json';
import ButtonList from './ButtonList';

// Helper function to extract unique chapter numbers for a given book
const getBookChapters = (bookName) => {
    const chapters = new Set();
    for (const item of BIBLE_DATA) {
        if (item.name.startsWith(bookName)) {
            const chapterMatch = item.name.match(/\s(\d+):/);
            if (chapterMatch && chapterMatch[1]) {
                chapters.add(chapterMatch[1]);
            }
        }
    }
    return Array.from(chapters).sort((a, b) => parseInt(a) - parseInt(b));
};

// Helper function to extract unique verse numbers for a given chapter
const getChapterVerses = (bookName, chapterNumber) => {
    const verses = new Set();
    const prefix = `${bookName} ${chapterNumber}:`;

    for (const item of BIBLE_DATA) {
        if (item.name.startsWith(prefix)) {
            const verseMatch = item.name.match(/:(\d+)$/);
            if (verseMatch && verseMatch[1]) {
                verses.add(verseMatch[1]);
            }
        }
    }
    return Array.from(verses).sort((a, b) => parseInt(a) - parseInt(b));
};


const ControlPanel = ({ customCallBack, setVerseData, setCurrentRef, setDrawMode, drawMode, clearAllHighlights }) => {
    // Selection states
    const [book, setBook] = useState('Genesis');
    const [chapter, setChapter] = useState('1');
    const [verse, setVerse] = useState('All'); // 'All' means full chapter view

    // Button List control states
    const [showButtonGroup, setShowButtonGroup] = useState(false);
    const [buttonValues, setButtonValues] = useState([]);
    const [buttonGroupFieldSetter, setButtonGroupFieldSetter] = useState(() => () => { });

    // Book lists
    const otBooks = [
        "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy", "Joshua", "Judges", "Ruth",
        "1 Samuel", "2 Samuel", "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles", "Ezra",
        "Nehemiah", "Esther", "Job", "Psalms", "Proverbs", "Ecclesiastes", "Song of Solomon",
        "Isaiah", "Jeremiah", "Lamentations", "Ezekiel", "Daniel", "Hosea", "Joel", "Amos",
        "Obadiah", "Jonah", "Micah", "Nahum", "Habakkuk", "Zephaniah", "Haggai", "Zechariah",
        "Malachi"
    ];
    const ntBooks = [
        "Matthew", "Mark", "Luke", "John", "Acts", "Romans", "1 Corinthians", "2 Corinthians",
        "Galatians", "Ephesians", "Philippians", "Colossians", "1 Thessalonians", "2 Thessalonians",
        "1 Timothy", "2 Timothy", "Titus", "Philemon", "Hebrews", "James", "1 Peter", "2 Peter",
        "1 John", "2 John", "3 John", "Jude", "Revelation"
    ];

    const availableChapters = useMemo(() => getBookChapters(book), [book]);
    const availableVerses = useMemo(() => ['All', ...getChapterVerses(book, chapter)], [book, chapter]);

    // Effect to auto-select the first chapter of a newly selected book
    useEffect(() => {
        if (availableChapters.length > 0 && chapter !== availableChapters[0]) {
            setChapter(availableChapters[0]);
        }
        setVerse('All');
        // eslint-disable-next-line
    }, [book, availableChapters]);

    // Function to fetch and display the content (returns an array of objects)
    const fetchAndDisplayContent = useCallback(() => {
        // 2. Clear canvas drawing (only if the function has been set by Drawing.jsx)
        if (typeof customCallBack === 'function') {
            customCallBack();
            setDrawMode(false);
            clearAllHighlights();
        }
        const versesArray = [];
        const searchPrefix = `${book} ${chapter}:`;

        if (verse === 'All') {
            // Display entire chapter
            for (const item of BIBLE_DATA) {
                if (item.name.startsWith(searchPrefix)) {
                    const verseNumber = item.name.split(':')[1];
                    versesArray.push({ verseNumber, text: item.verse });
                }
            }
        } else {
            // Display specific verse
            const exactName = `${searchPrefix}${verse}`;
            const item = BIBLE_DATA.find(i => i.name === exactName);
            if (item) {
                versesArray.push({ verseNumber: verse, text: item.verse });
            }
        }

        setVerseData(versesArray); // Pass the array to the parent state

        const reference = `${book} ${chapter}${verse !== 'All' ? ':' + verse : ''}`;
        setCurrentRef(reference);

        if (versesArray.length === 0) {
            setCurrentRef(`Could not find content for ${reference}.`);
        }

        setShowButtonGroup(false);
        // eslint-disable-next-line
    }, [book, chapter, verse, setVerseData, setCurrentRef]);

    useEffect(() => {
        fetchAndDisplayContent();
    }, [book, chapter, verse, fetchAndDisplayContent]);

    // Handle the click on OT, NT, Ch, Vs buttons
    const handleButtonGroup = (event) => {
        const id = event.target.id;
        let values = [];
        let setter = () => { };

        if (id === 'OT' || id === 'NT') {
            setter = setBook;
            values = id === 'OT' ? otBooks : ntBooks;
        } else if (id === 'Ch') {
            setter = setChapter;
            values = availableChapters;
        } else if (id === 'Vs') {
            setter = setVerse;
            values = availableVerses;
        }

        setButtonValues(values);
        setButtonGroupFieldSetter(() => setter);

        if (showButtonGroup && event.target.dataset.field === id) {
            setShowButtonGroup(false);
            event.target.dataset.field = '';
        } else {
            setShowButtonGroup(true);
            event.target.dataset.field = id;
        }
    };
    // FIX: Combined handler for touch/click events on the Draw/Exit button
    const handleDrawModeToggle = useCallback((e) => {
        // Prevent default behavior and stop event propagation 
        // to ensure the state only toggles once on touch devices.
        if (e.type === 'touchend' && e.cancelable) {
            // Prevent the subsequent 'click' event from firing
            e.preventDefault();
        }
        e.stopPropagation();
        setDrawMode(prev => !prev);
    }, [setDrawMode]);

    // FIX: Combined handler for touch/click events on the Clear button
    const handleClearHighlights = useCallback((e) => {
        if (e.type === 'touchend' && e.cancelable) {
            e.preventDefault();
        }
        customCallBack();
        e.stopPropagation();
        clearAllHighlights();
    }, [clearAllHighlights, customCallBack]);

    return (
        <div className='control-panel'>
            {showButtonGroup && (
                <div className="button-list-container">
                    <ButtonList
                        values={buttonValues}
                        setField={(value) => {
                            buttonGroupFieldSetter(value);
                            setShowButtonGroup(false);
                        }}
                    />
                </div>
            )}

            <div className='menu-content'>
                <button id="OT" onClick={handleButtonGroup} className={book && otBooks.includes(book) ? 'active' : ''}>OT</button>
                <button id="NT" onClick={handleButtonGroup} className={book && ntBooks.includes(book) ? 'active' : ''}>NT</button>
                <button id="Ch" onClick={handleButtonGroup} className={chapter ? 'active' : ''}>Ch: {chapter}</button>
                <button id="Vs" onClick={handleButtonGroup} className={verse !== 'All' ? 'active' : ''}>Vs: {verse}</button>

                {/* Updated Clear Button for mobile responsiveness */}
                <button
                    id="clear-highlights"
                    onClick={handleClearHighlights}
                    onTouchEnd={handleClearHighlights}
                >
                    Clear
                </button>

                {/* Updated Draw/Exit Button for mobile responsiveness (Fixes double-tap issue) */}
                <button
                    onClick={handleDrawModeToggle}
                    onTouchEnd={handleDrawModeToggle}
                    className="go-button"
                >
                    {drawMode ? 'Exit' : 'Draw'}
                </button>
            </div>
            <div className="current-selection">
                {book} - Chapter {chapter}{verse !== 'All' ? `, Verse ${verse}` : ''}
            </div>
        </div>
    );
};

export default ControlPanel;